import React from 'react';
import { AlertCircle, RefreshCw, Home, ShieldAlert, ChevronDown, ChevronUp } from 'lucide-react';

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, showDetails: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error('ErrorBoundary caught an unhandled exception:', error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    if (this.props.onReset) {
      this.props.onReset();
    } else {
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#030F1C] text-slate-100 flex flex-col justify-center items-center px-4 py-12 relative overflow-hidden font-sans">
          {/* Ambient Glows */}
          <div className="absolute top-10 left-10 w-96 h-96 bg-[#7A470E]/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#0B2644]/50 rounded-full blur-3xl pointer-events-none" />

          <div className="max-w-xl w-full bg-[#06182B] border-2 border-slate-800 rounded-3xl p-6 sm:p-8 shadow-2xl relative z-10 text-center">
            {/* Crest / Header */}
            <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border-2 border-amber-400/50 flex items-center justify-center mx-auto mb-4 text-amber-300">
              <ShieldAlert className="w-8 h-8" />
            </div>

            <span className="text-[11px] font-mono font-bold uppercase tracking-widest text-amber-400 bg-amber-950/60 px-3 py-1 rounded-full border border-amber-700/50">
              Session Boundary Recovery
            </span>

            <h1 className="text-xl sm:text-2xl font-black font-sc text-white mt-3 mb-2">
              Executive Portal Notice
            </h1>

            <p className="text-xs sm:text-sm text-slate-300 font-light leading-relaxed mb-6">
              A temporary display glitch was detected in this view. Your session data and bursary records remain fully secure.
            </p>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-6">
              <button
                onClick={this.handleReset}
                className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-gradient-to-r from-[#7A470E] to-[#0B2644] hover:from-amber-600 hover:to-blue-900 text-white text-xs font-bold font-sc flex items-center justify-center space-x-2 shadow-lg transition-all cursor-pointer"
              >
                <RefreshCw className="w-4 h-4 text-amber-300" />
                <span>Reload Workspace</span>
              </button>

              <button
                onClick={() => {
                  if (this.props.onNavigateHome) {
                    this.setState({ hasError: false, error: null, errorInfo: null });
                    this.props.onNavigateHome();
                  } else {
                    window.location.href = '/';
                  }
                }}
                className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-[#0B2644] hover:bg-[#082038] border border-slate-700 text-slate-200 text-xs font-bold font-sc flex items-center justify-center space-x-2 transition-all cursor-pointer"
              >
                <Home className="w-4 h-4 text-amber-300" />
                <span>Return to Home</span>
              </button>
            </div>

            {/* Diagnostic Details Toggle */}
            <div className="border-t border-slate-800 pt-4 text-left">
              <button
                onClick={() => this.setState({ showDetails: !this.state.showDetails })}
                className="flex items-center justify-between w-full text-[11px] font-mono text-slate-400 hover:text-slate-200 cursor-pointer"
              >
                <span className="flex items-center space-x-1.5">
                  <AlertCircle className="w-3.5 h-3.5 text-amber-400" />
                  <span>Technical Diagnostics</span>
                </span>
                {this.state.showDetails ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>

              {this.state.showDetails && (
                <div className="mt-3 p-3 bg-black/60 rounded-xl border border-slate-800 text-[11px] font-mono text-red-300 overflow-x-auto max-h-48 whitespace-pre-wrap">
                  {this.state.error?.toString()}
                  {this.state.errorInfo?.componentStack}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
